import {registerBrick, Query, Transaction, Config, Predicate} from 'olympe';
import { ReactBrick } from "@olympeio/core";
import { combineLatest } from 'rxjs';
import React, { useEffect, useState } from "react";
import { RichTreeViewPro } from '@mui/x-tree-view-pro/RichTreeViewPro';
import { IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, Divider, Paper, Box } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DocumentationArticle from './models/Documentation Article';
import DocumentationTreeModel from './models/Documentation Tree';

import { LicenseInfo } from '@mui/x-license';

export default class DocumentationTree extends ReactBrick {
    /**
     * @override
     */
    setupExecution($) {
        return combineLatest([
            $.observe('json', false),
            $.observe('doc id', false)
        ]);
    }

    init($) {
        super.init($);
    }

    /**
     * @override
     */
    static getReactComponent($) {
        return (props) => {
            try {
                const licenseKey = Config.getParameter('mui_license');
                if (licenseKey) {
                    LicenseInfo.setLicenseKey(licenseKey);
                }
            } catch (e) {
                console.warn('[DocumentationTree] MUI X license injection failed:', e);
            }

            const [json, docTag] = props.values;
            const [items, setItems] = useState([]);
            const [selectedItems, setSelectedItems] = useState(null);
            const [isDeleteOpen, setIsDeleteOpen] = useState(false);
            const [deleteTargetId, setDeleteTargetId] = useState(null);
            const [isNoTreeOpen, setIsNoTreeOpen] = useState(false);
            const [treeTag, setTreeTag] = useState('');

            React.useEffect(() => {
                const sub = $.observe('On Title Change').subscribe(async () => {
                    try {
                        // Fetch new JSON
                        let raw;
                        if (treeTag) {
                            const q = await Query.fromTag(treeTag, DocumentationTreeModel).execute();
                            raw = q.getFirst()?.getMUIJSONData?.();
                        } else if (docTag) {
                            const q = await Query.instancesOf(DocumentationTreeModel)
                                .filter(Predicate.equals(DocumentationTreeModel.idProp, docTag))
                                .execute();
                            raw = q.getFirst()?.getMUIJSONData?.();
                        }

                        // Normalize and set
                        let parsed = [];
                        if (Array.isArray(raw)) {
                            parsed = raw;
                        } else if (typeof raw === 'string') {
                            try {
                                parsed = JSON.parse(raw) || [];
                            } catch {
                                parsed = [];
                            }
                        } else if (raw && typeof raw === 'object') {
                            parsed = raw;
                        }

                        setItems(parsed);
                    } catch (e) {
                        console.error('[DocumentationTree] Failed to refresh items on title change:', e);
                    }
                });

                return () => sub?.unsubscribe?.();
            }, [$, treeTag, docTag]);

            React.useEffect(() => {
                if (!docTag) return;
                const fetchTree = async () => {
                    try {
                        const query = await Query.instancesOf(DocumentationTreeModel)
                            .filter(Predicate.equals(DocumentationTreeModel.idProp, docTag))
                            .execute();
                        const first = query.getFirst?.();
                        const tag = first?.getTag?.() ?? '';
                        setTreeTag(tag);
                        if (!first) {
                            console.warn('[DocumentationTree] No tree found for id:', docTag);
                        } else {
                            console.debug('[DocumentationTree] fetched treeTag:', tag);
                        }
                    } catch (err) {
                        console.error('Error fetching DocumentationTree by id:', err, docTag);
                        setTreeTag('');
                    }
                };
                fetchTree();
            }, [docTag]);

            useEffect(() => {
                try {
                    const parsed = json ? JSON.parse(json) : [];
                    setItems(parsed);

                    // Open "no tree" dialog if no data for given ID
                    if (!json || parsed.length === 0) {
                        setIsNoTreeOpen(true);
                    } else {
                        setIsNoTreeOpen(false);
                    }

                    // Automatically select the first element if present
                    if (parsed.length > 0) {
                        const firstId = parsed[0].tag_id;
                        setSelectedItems(firstId);
                        void handleItemClick(null, firstId);
                    }
                } catch (e) {
                    console.error('Invalid JSON for RichTreeView items:', e);
                    setItems([]);
                    // Also open the "no tree" dialog on invalid JSON
                    setIsNoTreeOpen(true);
                }
            }, [json]);

            // Transforms edited items into JSON and persists it
            const handleEdit = async (updatedItems) => {
                const updatedJson = JSON.stringify(updatedItems, null, 2);
                const t = new Transaction(true);
                DocumentationTreeModel.setMUIJSONData(t, treeTag, updatedJson);
                t.execute().then((res) => {
                    const createdObj = res.getCreatedObjects();
                    console.log("created", createdObj)
                }).catch((e) => {
                    console.error('Unable to save changes:', e);
                })
            };

            const clone = (obj) => JSON.parse(JSON.stringify(obj));

            // Removes an item by id
            const removeItemById = (nodes, id) => {
                const result = [];
                let removed = null;
                for (const node of nodes) {
                    if (node.tag_id === id) {
                        removed = { ...node };
                        continue;
                    }
                    if (node.children) {
                        const { removed: childRemoved, tree } = removeItemById(node.children, id);
                        if (childRemoved) {
                            removed = childRemoved;
                            result.push({ ...node, children: tree });
                        } else {
                            result.push(node);
                        }
                    } else {
                        result.push(node);
                    }
                }
                return { removed, tree: result };
            };

            // Inserts an item under parentId if parentId is null, inserts at root
            const insertItemAt = (nodes, parentId, index, item) => {
                if (parentId == null) {
                    const next = clone(nodes);
                    next.splice(index, 0, item);
                    return next;
                }
                return nodes.map((n) => {
                    if (n.tag_id === parentId) {
                        const children = Array.isArray(n.children) ? [...n.children] : [];
                        children.splice(index, 0, item);
                        return { ...n, children };
                    }
                    if (n.children) return { ...n, children: insertItemAt(n.children, parentId, index, item) };
                    return n;
                });
            };

            // Sets selected item and propagates to parent
            const handleItemClick = async (event, itemId) => {
                const findTag = (items, id) => {
                    for (const item of items) {
                        if (item.tag_id === id) return item;
                        if (item.children) {
                            const found = findTag(item.children, id);
                            if (found) return found;
                        }
                    }
                    return null;
                };

                const clickedTag = findTag(items, itemId);
                if (clickedTag) {
                    const queryResult = await Query.fromTag(clickedTag.tag_id, DocumentationArticle).execute();
                    const article = queryResult.getFirst();
                    $.set('selected article', article);
                    $.trigger('On Select');
                }
            };

            // Track selected items
            const handleSelectedItemsChange = (event, itemIds) => {
                setSelectedItems(itemIds);
            };

            // Drag and drop
            const handleItemPositionChange = (params) => {
                const { itemId, oldPosition, newPosition } = params;
                // Remove from old position
                const { removed, tree } = removeItemById(items, itemId);
                if (!removed) return;
                // Insert at new position
                const updated = insertItemAt(tree, newPosition.parentId ?? null, newPosition.index, removed);
                setItems(updated);
                void handleEdit(updated);
            };

            const openDeleteConfirm = (targetId) => {
                setDeleteTargetId(targetId);
                setIsDeleteOpen(true);
            };
            const closeDeleteConfirm = () => {
                setIsDeleteOpen(false);
                setDeleteTargetId(null);
            };
            const confirmDelete = () => {
                const id = deleteTargetId || (typeof selectedItems === 'string' ? selectedItems : (Array.isArray(selectedItems) && selectedItems.length === 1 ? selectedItems[0] : null));
                if (!id) { closeDeleteConfirm(); return; }
                const { removed, tree } = removeItemById(items, id);
                if (!removed) { closeDeleteConfirm(); return; }
                setItems(tree);
                void handleEdit(tree);
                closeDeleteConfirm();
            };

            // Add a new item
            const handleAddItem = () => {
                let createdObj;
                const t = new Transaction(true);
                const tag = DocumentationArticle.create(t);

                DocumentationArticle.setContent(t, tag, "");

                t.execute().then((res) => {
                    createdObj = res.getCreatedObjects()[0];
                    const id = createdObj.getTag();
                    const newItem = { tag_id: id, label: "New Item"};

                    setItems((prev) => {
                        const next = [...prev, newItem]; // always append at root as last element
                        void handleEdit(next);
                        return next;
                    });
                }).catch((e) => {
                    console.error('Unable to add new item:', e);
                });
            };

            // Delete the currently selected item
            const handleDeleteItem = () => {
                const selectedId = typeof selectedItems === 'string'
                    ? selectedItems
                    : (Array.isArray(selectedItems) && selectedItems.length === 1 ? selectedItems[0] : null);
                if (!selectedId) return;
                openDeleteConfirm(selectedId);
            };


            return (
                <div style={{ width: '100%', height: '100%' }}>
                    <style>{`
                      .MuiTreeItem-content .MuiTreeItem-label { color: #808080 !important; }
                    `}</style>
                    <Paper variant="outlined" sx={{
                        borderRadius: 1,
                        overflow: 'hidden',
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        boxSizing: 'border-box',
                        border: '1px solid',
                        borderColor: 'divider'
                    }}>
                        <Box sx={{ '& .MuiDataGrid-licenseInfo, & .MuiWatermark-root': { pointerEvents: 'none' } }} />
                        <Box sx={{ p: 1.5, flex: 1, overflow: 'auto' }}>
                            <RichTreeViewPro
                                key={json || 'empty-json'}
                                items={items}
                                itemsReordering
                                getItemId={(item) => String(item.tag_id)}
                                getItemLabel={(item) => (typeof item.label === 'string' ? item.label : String(item.label))}
                                getItemChildren={(item) => item.children}
                                onItemClick={handleItemClick}
                                onItemPositionChange={handleItemPositionChange}
                                onSelectedItemsChange={handleSelectedItemsChange}
                                experimentalFeatures={{ indentationAtItemLevel: true, itemsReordering: true }}
                            />
                        </Box>
                        <Divider />
                        <Box sx={{ p: 0.5, display: 'flex', alignItems: 'center' }}>
                            <Tooltip title="Add item">
                                <span>
                                    <IconButton color="primary" onClick={handleAddItem} size="small">
                                        <AddIcon />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Box sx={{ flex: 1 }} />
                            <Tooltip title={ (typeof selectedItems === 'string' || (Array.isArray(selectedItems) && selectedItems.length > 0)) ? 'Delete selected' : 'Select an item to delete' }>
                              <span>
                                <IconButton
                                  color="error"
                                  onClick={handleDeleteItem}
                                  size="small"
                                  disabled={!(typeof selectedItems === 'string' || (Array.isArray(selectedItems) && selectedItems.length > 0))}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                        </Box>
                    </Paper>
                    <Dialog open={isDeleteOpen} onClose={closeDeleteConfirm} maxWidth="xs" fullWidth>
                        <DialogTitle>Delete item?</DialogTitle>
                        <DialogContent>
                            <DialogContentText>
                                This action will remove the selected item and all of its children. This cannot be undone.
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={closeDeleteConfirm}>Cancel</Button>
                            <Button color="error" variant="contained" onClick={confirmDelete}>Delete</Button>
                        </DialogActions>
                    </Dialog>
                    <Dialog open={isNoTreeOpen} onClose={() => setIsNoTreeOpen(false)} maxWidth="xs" fullWidth>
                        <DialogTitle>No documentation tree</DialogTitle>
                        <DialogContent>
                            <DialogContentText>
                                No documentation tree found for given ID.
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setIsNoTreeOpen(false)}>Close</Button>
                        </DialogActions>
                    </Dialog>
                </div>
            );
        };
    }
}

registerBrick('0199867068aa98a9fb53', DocumentationTree);
